import type { AIModelChoices, Evidence, SimpleMaterialQualityCategory } from 'api';
import { useState } from 'react';
import { isVideo, MediaDialog, type PhotoEvidenceItem, PhotoEvidenceList } from 'ui';

import {
  AiFeedbackVisibilityProvider,
  AiFiltersAndBreakdownSidebar,
  AnnotatedMediaItem,
  useAnnotations,
} from '../../../annotations';

const getLatestRunAIVersion = (evidence: Evidence | null) => evidence?.model_results?.outputs?.[0]?.safi_ai_version;

export type PhotoEvidenceGalleryWithDialogProps = {
  evidence: Evidence[];
  isLoading?: boolean;
  materialCategory?: Pick<SimpleMaterialQualityCategory, 'id' | 'name'> | null;
};

export const PhotoEvidenceGalleryWithDialog = ({
  evidence,
  isLoading,
  materialCategory,
}: PhotoEvidenceGalleryWithDialogProps) => {
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);

  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
  } = useAnnotations({});

  const validEvidence = evidence.filter(
    (item): item is Evidence & { file: string; preview: string } => Boolean(item.file) || Boolean(item.preview)
  );

  const evidencePhotos: PhotoEvidenceItem[] = validEvidence.map((item) => ({
    src: item.file,
    thumbnailSrc: item.preview,
  }));

  const dialogMedia = validEvidence.map((item) => item.file);

  const handlePhotoClick = (photo: PhotoEvidenceItem): void => {
    if (photo.src) {
      const relatedEvidence = validEvidence.find((item) => item.file === photo.src || item.preview === photo.src);
      setSelectedEvidence(relatedEvidence || null);
      if (relatedEvidence?.model_results?.coco_prediction) {
        handleImageChange([...relatedEvidence.model_results.coco_prediction]);
      }
    }
  };

  const handleDialogClose = () => {
    setSelectedEvidence(null);
  };

  const handleNavigate = (src: string) => {
    const relatedEvidence = validEvidence.find((item) => item.file === src || item.preview === src);
    setSelectedEvidence(relatedEvidence || null);
    if (relatedEvidence?.model_results?.coco_prediction) {
      handleImageChange([...relatedEvidence.model_results.coco_prediction]);
    }
  };

  return (
    <>
      <PhotoEvidenceList
        evidencePhotos={evidencePhotos}
        openOnClick={true}
        handleClick={handlePhotoClick}
        isLoading={isLoading}
        slotProps={{
          imageList: {
            cols: 2,
          },
        }}
      />

      <AiFeedbackVisibilityProvider>
        <MediaDialog
          open={Boolean(selectedEvidence?.file)}
          selectedMedium={selectedEvidence?.file ?? ''}
          onClose={handleDialogClose}
          media={dialogMedia}
          handleNavigate={handleNavigate}
          imageCount={dialogMedia.length}
          slotProps={{
            imageCarousel: {
              slotProps: {
                ...(selectedAnnotation && selectedEvidence && selectedEvidence.model_results?.model_name
                  ? {
                      mediaItemSlot: isVideo(selectedEvidence.file ?? '') ? null : (
                        <AnnotatedMediaItem
                          image={selectedEvidence.file ?? ''}
                          annotations={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          showLegend={showLegend}
                          model={selectedEvidence.model_results?.model_name as AIModelChoices}
                          showAerosols={true}
                          evidenceId={selectedEvidence.id}
                        />
                      ),
                      sidebar: {
                        children: (
                          <AiFiltersAndBreakdownSidebar
                            selectedAnnotation={selectedAnnotation}
                            hiddenCategories={hiddenCategories}
                            evidenceId={selectedEvidence.id}
                            handleHideCategory={handleHideCategory}
                            handleToggleLegend={handleToggleLegend}
                            evaluatedSpecs={selectedEvidence.evaluated_specs ?? undefined}
                            latestRunAIVersion={getLatestRunAIVersion(selectedEvidence)}
                            showLegend={showLegend}
                            model={selectedEvidence.model_results?.model_name as AIModelChoices}
                            showAerosols={true}
                            aluminiumComposition={selectedEvidence.aluminium_composition ?? undefined}
                            hdpeComposition={selectedEvidence.hdpe_composition ?? undefined}
                            petComposition={
                              selectedEvidence.pet_composition ?? selectedEvidence.pet_loose_composition ?? undefined
                            }
                            materialCategory={materialCategory}
                          />
                        ),
                      },
                    }
                  : {
                      mediaItemSlot: isVideo(selectedEvidence?.file ?? '') ? null : (
                        <AnnotatedMediaItem
                          image={selectedEvidence?.file ?? ''}
                          annotations={[]}
                          model={'alu'} // Not actually used since annotations are falsy
                          evidenceId={0} // Not actually used since annotations are falsy
                        />
                      ),
                    }),
              },
            },
          }}
        />
      </AiFeedbackVisibilityProvider>
    </>
  );
};
